// Form-/detail-oldal konténere: BALRA igazított, keskeny hasáb, SAJÁT görgetéssel.
//
// MIÉRT NEM a `PageBody`: az `mx-auto max-w-[1440px]`-gyel KÖZÉPRE teszi a tartalmat és széles hasábot ad —
// egy űrlapnak egyik sem jó. Ez a konténer a PROFIL-OLDAL bevált mintáját általánosítja: a tartalom a
// shell `main`-jén belül a maga `overflow-y-auto` sávjában görög, tehát desktopon pontosan a sidebar
// magasságáig fut (a `h-full` + `min-h-0` páros nélkül a flex-gyerek nem zsugorodna, és a lap egésze görögne).
//
// A shellhez NEM nyúlunk — ez kizárólag a tartalom-oldali konténer.
//
// `aside`: opcionális, RAGADÓS oldalsáv az űrlap-hasáb MELLETTI üres helyen (ma a szekció-navigáció).
// `lg` alatt rejtett — keskeny kijelzőn nincs hova tenni, és a görgetés ott természetes.
import { createContext, useContext, useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * A GÖRGETÉS-KONTÉNER eleme — az `aside` tartalmának (pl. `SectionNav`) SZÜKSÉGE van rá.
 *
 * MIÉRT: itt nem az ablak görög, hanem ez a belső sáv. Egy `IntersectionObserver`, aminek a `root`-ja az
 * ablak, ebben a konténerben MINDIG rosszul számol (a szekciók „mindig láthatók"). A konténert nem
 * találgatni kell (DOM-felfelé kúszással), hanem a tulajdonosa adja át — ezért van ez a context.
 */
const ScrollRootContext = createContext<HTMLElement | null>(null);

/** A `FormPage` görgetés-konténere (vagy `null`, ha a hívó nincs `FormPage`-en belül). */
export function useFormPageScrollRoot(): HTMLElement | null {
  return useContext(ScrollRootContext);
}

export function FormPage({
  children,
  className,
  aside,
  tight = false,
}: {
  children: ReactNode;
  className?: string;
  /** Ragadós oldalsáv az űrlap mellett (pl. `SectionNav`). `lg` alatt nem jelenik meg. */
  aside?: ReactNode;
  /**
   * SZŰKEBB felső/alsó térköz (12px a 20/24px helyett). Azokon a részlet-lapokon kell, amelyek NAGY címmel
   * indulnak (jegy, számla): ott a bővebb köz optikailag „lelógatta" a lapot a sidebar első menüpontjához
   * képest (user-lelet, — mérve: nav-elem teteje 73, a tartalom 84 → 12px-es közzel 72).
   */
  tight?: boolean;
}): JSX.Element {
  // `useState` (nem `useRef`) a callback-refhez: a ref-hozzárendelés NEM vált ki újrarenderelést, így a
  // context első körben `null` maradna, és az `aside` sosem kapná meg a konténert.
  const [scrollRoot, setScrollRoot] = useState<HTMLElement | null>(null);
  return (
    <div className="flex h-full flex-col">
      {/* `min-h-0`: e nélkül a flex-gyerek nem tud a tartalmánál kisebb lenni → nem ő görögne, hanem a lap. */}
      {/* `relative`: a TÉNYLEGES görgető legyen az abszolút leszármazottak containing blockja — így a
          tartalomhoz kötött elemek vele görögnek, és nem szöknek ki a levágásból (lásd `app-shell` `main`). */}
      <div className="relative min-h-0 flex-1 overflow-y-auto" ref={setScrollRoot}>
        {/* A vízszintes padding SZÁNDÉKOSAN FORDÍTOTT a szokásoshoz képest (mobilon több, desktopon kevesebb) —
            NE „javítsd" md:px-6-ra:
              • desktop `md:px-4` (16px): a `main` a 256px-en kezdődik, +16 = 272 → PONTOSAN ott, ahol a topbar
                morzsa-címe kezdődik. Így az input-mezők, szekció-címek és a cím egy függőleges vonalra esnek.
              • mobil `px-6` (24px): ott a topbar-címhez igazítani értelmetlen (a hamburger után, 62px-nél van),
                viszont a bőségesebb margó olvashatóbb a keskeny hasábon.
            Mérve: 1440-en cím=272 és tartalom=272; 375-ön tartalom=24. */}
        <div
          className={cn(
            'flex gap-10 px-6 md:px-4',
            // A FELSŐ/ALSÓ térköz: `tight` → 12px (a lap-tartalom TETEJE így a sidebar ELSŐ menüpontjának
            // tetejével egy vonalban áll — mérve 1440-en: nav-elem 73, tartalom 72), egyébként a bővebb
            // 20/24px. MIÉRT OPT-IN, és miért nem globális (user-döntés): a szűkebb köz ott
            // helyes, ahol a lap NAGY címmel indul (jegy-, számla-részlet) — az ügyfél-/fiók-részlet
            // viszont kis, overline szekció-címmel kezdődik, ott a bővebb köz a jó („jó így, ahogy most van").
            tight ? 'py-3' : 'py-5 md:py-6',
          )}
        >
          <div className={cn('min-w-0 max-w-3xl flex-1', className)}>{children}</div>
          {aside !== undefined && (
            <ScrollRootContext.Provider value={scrollRoot}>
              {/* `shrink-0`: a rail SOSEM szorítja össze az űrlapot — inkább ő tűnik el (lg alatt). */}
              <div className="hidden shrink-0 lg:block">{aside}</div>
            </ScrollRootContext.Provider>
          )}
        </div>
      </div>
    </div>
  );
}
